"use client";

import React, { useState } from "react";
import { Tabs } from "../../data/website-development";
import { useServiceData } from "../../hooks/useServiceData";

const BRAND = "#F48428";

export default function FeaturesTabs({ dynamicData }) {
  const { data: backendPageData } = useServiceData();

  // Requirement: Integrate dynamic data into the existing section
  const sourceData = dynamicData || backendPageData?.extraSections?.find(s => s.type === "featureGridSection")?.refId;

  // Normalize dynamicData if it comes from featureGridSection (cards), processSection (steps), or features (items)
  const rawTabs = sourceData?.tabs || sourceData?.cards || sourceData?.steps || sourceData?.items || [];

  const finalTabs = rawTabs.length > 0
    ? rawTabs.map((t, idx) => ({
      key: t.key || t.title?.toLowerCase().replace(/\s+/g, "-") || `tab-${idx}`,
      label: t.label || t.title || t.name || `Tab ${idx + 1}`,
      title: t.title || t.name || "",
      desc: t.desc || t.description || "",
      bullets: t.bullets || t.points?.map(p => typeof p === 'string' ? p : (p.text || p.title)) || [],
      icon: t.icon || "fa-solid fa-rocket"
    }))
    : Tabs;

  const sectionTitle = sourceData?.title || "We are the Experts";
  const sectionSubtitle = sourceData?.subtitle || "We Go Beyond the Basics";

  const [active, setActive] = useState(finalTabs[0]?.key || "seo");
  const [displayKey, setDisplayKey] = useState(finalTabs[0]?.key || "seo");
  const [fade, setFade] = useState(1);
  const [dir, setDir] = useState(1); // 1 -> forward, -1 -> backward

  const changeTab = (key) => {
    if (key === active) return;
    // determine direction for slide
    const oldIndex = finalTabs.findIndex((t) => t.key === active);
    const newIndex = finalTabs.findIndex((t) => t.key === key);
    setDir(newIndex > oldIndex ? 1 : -1);
    setActive(key);
    // fade out current then swap content and fade in
    setFade(0);
    setTimeout(() => {
      setDisplayKey(key);
      setFade(1);
    }, 260);
  };

  const current = finalTabs.find((t) => t.key === displayKey) || finalTabs[0];

  const pillBase = {
    padding: "14px 16px",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.1)",
  };

  return (
    <section className="py-5">
      <div className="container">
        {/* Section heading (shared) so right-side content starts level with tabs */}
        <div className="mb-4">
          <div
            className="text-uppercase"
            style={{ letterSpacing: ".18em", color: BRAND }}
          >
            {sectionTitle}
          </div>
          <h2 className="display-5 fw-bold text-white m-0">
            {sectionSubtitle}
          </h2>
        </div>

        <div className="row g-4 align-items-start">
          {/* Left: tabs */}
          <div className="col-12 col-lg-6 col-xl-5">
            <div className="d-flex flex-column gap-3">
              {finalTabs.map((t) => {
                const activePill = t.key === active;
                return (
                  <div
                    key={t.key}
                    onClick={() => changeTab(t.key)}
                    style={{
                      ...pillBase,
                      background: activePill
                        ? "#0e0e0e"
                        : "rgba(255,255,255,0.06)",
                      color: activePill ? "#fff" : "#e7e7e7",
                      boxShadow: activePill
                        ? "inset 0 0 0 2px #000, 0 4px 16px rgba(0,0,0,0.25)"
                        : "none",
                    }}
                  >
                    <i
                      className={t.icon}
                      style={{ color: activePill ? "#fff" : "#cfcfcf" }}
                    ></i>
                    <span>{t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: content */}
          <div className="col-12 col-lg-6 col-xl-7">
            <div
              className="text-uppercase mb-2"
              style={{ letterSpacing: ".18em", color: BRAND }}
            >
              Tailored Excellence
            </div>
            <div
              style={{
                opacity: fade,
                transform: fade
                  ? "translate(0,0) scale(1)"
                  : `translate(${dir * 18}px, 6px) scale(0.995)`,
                transition:
                  "opacity 420ms cubic-bezier(.2,.7,.3,1), transform 420ms cubic-bezier(.2,.7,.3,1)",
              }}
              key={`panel-${current.key}`}
            >
              <h3 className="fw-bold text-white">{current.title}</h3>
              <p className="text-secondary" style={{ maxWidth: 720 }}>
                {current.desc}
              </p>

              <div className="row g-3 mt-2">
                {current.bullets.map((b, i) => (
                  <div
                    className="col-12 col-md-6"
                    key={`${current.key}-${i}`}
                    style={{
                      opacity: fade,
                      transform: fade ? "translateY(0)" : "translateY(8px)",
                      transition: `opacity 480ms ${120 + i * 120
                        }ms cubic-bezier(.2,.7,.3,1), transform 480ms ${120 + i * 120
                        }ms cubic-bezier(.2,.7,.3,1)`,
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          border: `1px solid ${BRAND}`,
                          color: BRAND,
                        }}
                      >
                        <i
                          className="fa-solid fa-check"
                          style={{ fontSize: 12 }}
                        ></i>
                      </span>
                      <span className="text-white">{b}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
